import { useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { FormField } from 'v5/design-system/Form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from 'v5/design-system/Select';
import { useFieldContext } from '../FieldContext';
import { EmptyField } from './EmptyField';
import { FieldControlBase } from './FieldControlBase';
import type { FieldComponentProps } from './fieldComponent.types';

/**
 * Pronouns select (v5 `createPronounsControl`): option ids as strings,
 * `string | null` value, always clearable.
 */
export function PronounsField({
  spec,
  path,
  hideLabel
}: FieldComponentProps<'pronouns'>) {
  const { t } = useTranslation('ds');
  const ctx = useFieldContext();
  const readOnly = ctx.mode === 'display';
  const selected: string = useWatch({ name: path }) ?? '';

  if (readOnly && selected === '') {
    return <EmptyField spec={spec} path={path} hideLabel={hideLabel} />;
  }

  return (
    <FormField
      name={path}
      render={({ field: { onChange, value, ref, onBlur, name } }) => (
        <FieldControlBase spec={spec} hideLabel={hideLabel}>
          <Select
            name={name}
            value={value ?? ''}
            onValueChange={onChange}
            clearable
            onClear={() => onChange(null)}
          >
            <SelectTrigger
              variant={ctx.variant}
              size={ctx.size}
              readOnly={readOnly}
              ref={ref}
              onBlur={onBlur}
            >
              <SelectValue placeholder={t('Select.emptyPlaceholder')} />
            </SelectTrigger>
            <SelectContent>
              {spec.options.map(option => (
                <SelectItem key={option.id} value={String(option.id)}>
                  {option.value}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FieldControlBase>
      )}
    />
  );
}
